import React, { useState } from 'react';
import { UserConversation } from '@/components/Translation/UserConversation';
import type { SessionEvent } from '@/services/interfaces/SessionEvent';
import { Button } from '@/components/ui/button';
import { Bug, CornerUpLeft, Loader2 } from 'lucide-react';
import { toast } from "sonner";
import { useWebRTCSession } from '@/contexts/WebRTCSessionContext';
import { FreeTrialNoticeBanner } from '@/components/shared/FreeTrialNoticeBanner';
import { useTranslation } from '@/contexts/I18nContext';

interface TranslatingPageProps {
  // isSessionActive: boolean;
  // sessionConnectionStatus: SessionConnectionStatus;
  // events: SessionEvent[];
}

// 示例事件数据 - 用于调试
const EXAMPLE_EVENTS = `[
  {
    "type": "conversation.item.created",
    "event_id": "event_BbfdnNBs3zyzvlogyr3Dz",
    "previous_item_id": "item_BbfdjWxAe3EBrYEsmjmPU",
    "item": {
      "id": "item_BbfdlvjLfjXA6iAhE56tY",
      "object": "realtime.item",
      "type": "message",
      "status": "completed",
      "role": "user",
      "content": [{"type": "input_audio", "transcript": null}]
    },
    "timestamp": "2025-05-27T03:51:42.178Z"
  },
  {
    "type": "conversation.item.input_audio_transcription.completed",
    "event_id": "event_BbfdnDliEAX6WeCOqGbyj",
    "item_id": "item_BbfdlvjLfjXA6iAhE56tY",
    "content_index": 0,
    "transcript": "Go on to practice.",
    "timestamp": "2025-05-27T03:51:42.366Z"
  },
  {
    "type": "conversation.item.created",
    "event_id": "event_Bbfdn8itmazK581PcD3UH",
    "previous_item_id": "item_BbfdlvjLfjXA6iAhE56tY",
    "item": {
      "id": "item_Bbfdny0v6g3QD3wTlw2LS",
      "object": "realtime.item",
      "type": "message",
      "status": "in_progress",
      "role": "assistant",
      "content": []
    },
    "timestamp": "2025-05-27T03:51:42.584Z"
  },
  {
    "type": "response.audio_transcript.delta",
    "event_id": "event_BbfdnQ6mR4oGBKzadaePG",
    "response_id": "resp_BbfdnrwqrZavbG6gNwrOW",
    "item_id": "item_Bbfdny0v6g3QD3wTlw2LS",
    "output_index": 0,
    "content_index": 0,
    "delta": "[",
    "timestamp": "2025-05-27T03:51:42.587Z"
  },
  {
    "type": "response.audio_transcript.delta",
    "event_id": "event_BbfdntwZs1kDq5XDsfeVB",
    "response_id": "resp_BbfdnrwqrZavbG6gNwrOW",
    "item_id": "item_Bbfdny0v6g3QD3wTlw2LS",
    "output_index": 0,
    "content_index": 0,
    "delta": "cm",
    "timestamp": "2025-05-27T03:51:42.598Z"
  },
  {
    "type": "response.audio_transcript.delta",
    "event_id": "event_BbfdnXXXXXXXXXXXXXXXX",
    "response_id": "resp_BbfdnrwqrZavbG6gNwrOW",
    "item_id": "item_Bbfdny0v6g3QD3wTlw2LS",
    "output_index": 0,
    "content_index": 0,
    "delta": "n] 当然可以，你想练习哪些单词？",
    "timestamp": "2025-05-27T03:51:42.700Z"
  },
  {
    "type": "response.output_item.done",
    "event_id": "event_BbfdovasxBU0ld7p48G8g",
    "response_id": "resp_BbfdnrwqrZavbG6gNwrOW",
    "output_index": 0,
    "item": {
      "id": "item_Bbfdny0v6g3QD3wTlw2LS",
      "object": "realtime.item",
      "type": "message",
      "status": "completed",
      "role": "assistant",
      "content": [{
        "type": "audio",
        "transcript": "[cmn] 当然可以，你想练习哪些单词？"
      }]
    },
    "timestamp": "2025-05-27T03:51:43.341Z"
  },
  {
    "type": "response.done",
    "event_id": "event_BbfdoO5in6osMv5jkTVex",
    "response": {
      "object": "realtime.response",
      "id": "resp_BbfdnrwqrZavbG6gNwrOW",
      "status": "completed",
      "output": [{
        "id": "item_Bbfdny0v6g3QD3wTlw2LS",
        "object": "realtime.item",
        "type": "message",
        "status": "completed",
        "role": "assistant",
        "content": [{
          "type": "audio",
          "transcript": "[cmn] 当然可以，你想练习哪些单词？"
        }]
      }],
      "usage": {
        "total_tokens": 1316,
        "input_tokens": 1211,
        "output_tokens": 105,
        "input_token_details": {
          "text_tokens": 570,
          "audio_tokens": 641,
          "cached_tokens": 1024,
          "cached_tokens_details": {
            "text_tokens": 512,
            "audio_tokens": 512
          }
        },
        "output_token_details": {
          "text_tokens": 27,
          "audio_tokens": 78
        }
      }
    },
    "timestamp": "2025-05-27T03:51:43.341Z"
  }
]`;

export const TranslatingPage: React.FC<TranslatingPageProps> = ({
  // isSessionActive,
  // sessionConnectionStatus,
  // events,
}) => {
  const { t } = useTranslation();

  // 调试面板状态
  const [showDebugPanel, setShowDebugPanel] = useState(false);
  // const [localDebugEvents, setLocalDebugEvents] = useState<SessionEvent[]>([]);
  const [eventsJson, setEventsJson] = useState(EXAMPLE_EVENTS);
  const [parseError, setParseError] = useState<string | null>(null);


  const { injectTestEvents, clearTestEvents, rtcEvents, stopTranslation, isStopping } = useWebRTCSession();

  // const [conversationKey, setConversationKey] = useState(0); // 用于强制重新渲染 UserConversation

  // 获取语言配置用于导出功能
  // const { sourceLanguage, targetLanguage } = useLanguage();
  // const languageConfig = useMemo(() => ({
  //   sourceLanguage,
  //   targetLanguage
  // }), [sourceLanguage, targetLanguage]);

  // 决定使用哪个事件数组：如果有调试事件则使用调试事件，否则使用传入的事件
  // const displayEvents = localDebugEvents.length > 0 ? localDebugEvents : events;
  
  // 🔑 处理返回操作（与暂停按钮逻辑相同）
  const handleBack = async () => {
    try {
      await stopTranslation();
    } catch (err) {
      console.error("Failed to stop translation:", err);
    }
  };

  // 处理事件解析
  const handleParseEvents = () => {
    try {
      const parsedEvents = JSON.parse(eventsJson) as SessionEvent[];
      injectTestEvents(parsedEvents);
      // setLocalDebugEvents(parsedEvents);
      setParseError(null);
      // setConversationKey(prev => prev + 1); // 强制重新渲染
      toast.success(`${t('debug.testAndParse')}成功！(${parsedEvents.length} 个事件)`);
      console.log('🧪 [TranslatingPage] 解析事件成功:', parsedEvents);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t('debug.parseError');
      setParseError(errorMessage);
      toast.error(`${t('debug.parseError')}: ${errorMessage}`);
      console.error('🧪 [TranslatingPage] 解析事件失败:', error);
    }
  };

  // 清除调试事件
  const handleClearEvents = () => {
    // setLocalDebugEvents([]);
    setParseError(null);
    // setConversationKey(prev => prev + 1); // 强制重新渲染
    clearTestEvents();
  };

  // 加载"落单"场景的测试用例
  const handleLoadOrphanExample = () => {
    const orphanEvents = `[
  {
    "type": "conversation.item.created",
    "event_id": "event_1",
    "previous_item_id": null,
    "item": {
      "id": "item_X1_orphan",
      "object": "realtime.item",
      "type": "message",
      "status": "completed",
      "role": "user",
      "content": [{"type": "input_audio", "transcript": null}]
    },
    "timestamp": "2025-05-27T03:51:40.000Z"
  },
  {
    "type": "conversation.item.input_audio_transcription.completed",
    "event_id": "event_2",
    "item_id": "item_X1_orphan",
    "content_index": 0,
    "transcript": "Of course, which word?",
    "timestamp": "2025-05-27T03:51:40.778Z"
  },
  {
    "type": "conversation.item.created",
    "event_id": "event_3",
    "previous_item_id": "item_X1_orphan",
    "item": {
      "id": "item_X2_continuation",
      "object": "realtime.item",
      "type": "message",
      "status": "completed",
      "role": "user",
      "content": [{"type": "input_audio", "transcript": null}]
    },
    "timestamp": "2025-05-27T03:51:42.178Z"
  },
  {
    "type": "conversation.item.input_audio_transcription.completed",
    "event_id": "event_4",
    "item_id": "item_X2_continuation",
    "content_index": 0,
    "transcript": "Go on to practice.",
    "timestamp": "2025-05-27T03:51:42.366Z"
  },
  {
    "type": "conversation.item.created",
    "event_id": "event_5",
    "previous_item_id": "item_X2_continuation",
    "item": {
      "id": "item_Assistant1",
      "object": "realtime.item",
      "type": "message",
      "status": "in_progress",
      "role": "assistant",
      "content": []
    },
    "timestamp": "2025-05-27T03:51:42.584Z"
  },
  {
    "type": "response.audio_transcript.delta",
    "event_id": "event_6",
    "response_id": "resp_1",
    "item_id": "item_Assistant1",
    "output_index": 0,
    "content_index": 0,
    "delta": "[cmn] 当然可以，",
    "timestamp": "2025-05-27T03:51:42.598Z"
  },
  {
    "type": "response.audio_transcript.delta",
    "event_id": "event_7",
    "response_id": "resp_1",
    "item_id": "item_Assistant1",
    "output_index": 0,
    "content_index": 0,
    "delta": "你想练习哪些单词？",
    "timestamp": "2025-05-27T03:51:42.700Z"
  },
  {
    "type": "response.done",
    "event_id": "event_8",
    "response": {
      "object": "realtime.response",
      "id": "resp_1",
      "status": "completed",
      "output": [{
        "id": "item_Assistant1",
        "object": "realtime.item",
        "type": "message",
        "status": "completed",
        "role": "assistant",
        "content": [{
          "type": "audio",
          "transcript": "[cmn] 当然可以，你想练习哪些单词？"
        }]
      }],
      "usage": {
        "total_tokens": 1316,
        "input_tokens": 1211,
        "output_tokens": 105
      }
    },
    "timestamp": "2025-05-27T03:51:43.341Z"
  }
]`;
    setEventsJson(orphanEvents);
    setParseError(null);
  };

  // 加载 User A 合并测试用例（英文用户的连续语音）
  const handleLoadUserAMergeExample = () => {
    const userAMergeEvents = `[
  {
    "type": "conversation.item.created",
    "event_id": "event_1",
    "previous_item_id": null,
    "item": {
      "id": "item_UserA_1",
      "object": "realtime.item",
      "type": "message",
      "status": "completed",
      "role": "user",
      "content": [{"type": "input_audio", "transcript": null}]
    },
    "timestamp": "2025-05-27T03:51:40.000Z"
  },
  {
    "type": "conversation.item.input_audio_transcription.completed",
    "event_id": "event_2",
    "item_id": "item_UserA_1",
    "content_index": 0,
    "transcript": "Hello, I would like to",
    "timestamp": "2025-05-27T03:51:40.778Z"
  },
  {
    "type": "conversation.item.created",
    "event_id": "event_3",
    "previous_item_id": "item_UserA_1",
    "item": {
      "id": "item_UserA_2",
      "object": "realtime.item",
      "type": "message",
      "status": "completed",
      "role": "user",
      "content": [{"type": "input_audio", "transcript": null}]
    },
    "timestamp": "2025-05-27T03:51:42.178Z"
  },
  {
    "type": "conversation.item.input_audio_transcription.completed",
    "event_id": "event_4",
    "item_id": "item_UserA_2",
    "content_index": 0,
    "transcript": "practice my Chinese speaking skills.",
    "timestamp": "2025-05-27T03:51:42.366Z"
  },
  {
    "type": "conversation.item.created",
    "event_id": "event_5",
    "previous_item_id": "item_UserA_2",
    "item": {
      "id": "item_Assistant1",
      "object": "realtime.item",
      "type": "message",
      "status": "in_progress",
      "role": "assistant",
      "content": []
    },
    "timestamp": "2025-05-27T03:51:42.584Z"
  },
  {
    "type": "response.audio_transcript.delta",
    "event_id": "event_6",
    "response_id": "resp_1",
    "item_id": "item_Assistant1",
    "output_index": 0,
    "content_index": 0,
    "delta": "[cmn] 好的，",
    "timestamp": "2025-05-27T03:51:42.598Z"
  },
  {
    "type": "response.audio_transcript.delta",
    "event_id": "event_7",
    "response_id": "resp_1",
    "item_id": "item_Assistant1",
    "output_index": 0,
    "content_index": 0,
    "delta": "我们开始练习中文口语吧！",
    "timestamp": "2025-05-27T03:51:42.700Z"
  },
  {
    "type": "response.done",
    "event_id": "event_8",
    "response": {
      "object": "realtime.response",
      "id": "resp_1",
      "status": "completed",
      "output": [{
        "id": "item_Assistant1",
        "object": "realtime.item",
        "type": "message",
        "status": "completed",
        "role": "assistant",
        "content": [{
          "type": "audio",
          "transcript": "[cmn] 好的，我们开始练习中文口语吧！"
        }]
      }],
      "usage": {
        "total_tokens": 1316,
        "input_tokens": 1211,
        "output_tokens": 105
      }
    },
    "timestamp": "2025-05-27T03:51:43.341Z"
  }
]`;
    setEventsJson(userAMergeEvents);
    setParseError(null);
  };

  // 加载 User B 合并测试用例（中文用户的连续语音）
  const handleLoadUserBMergeExample = () => {
    const userBMergeEvents = `[
  {
    "type": "conversation.item.created",
    "event_id": "event_1",
    "previous_item_id": null,
    "item": {
      "id": "item_UserB_1",
      "object": "realtime.item",
      "type": "message",
      "status": "completed",
      "role": "user",
      "content": [{"type": "input_audio", "transcript": null}]
    },
    "timestamp": "2025-05-27T03:51:40.000Z"
  },
  {
    "type": "conversation.item.input_audio_transcription.completed",
    "event_id": "event_2",
    "item_id": "item_UserB_1",
    "content_index": 0,
    "transcript": "你好，我想要",
    "timestamp": "2025-05-27T03:51:40.778Z"
  },
  {
    "type": "conversation.item.created",
    "event_id": "event_3",
    "previous_item_id": "item_UserB_1",
    "item": {
      "id": "item_UserB_2",
      "object": "realtime.item",
      "type": "message",
      "status": "completed",
      "role": "user",
      "content": [{"type": "input_audio", "transcript": null}]
    },
    "timestamp": "2025-05-27T03:51:42.178Z"
  },
  {
    "type": "conversation.item.input_audio_transcription.completed",
    "event_id": "event_4",
    "item_id": "item_UserB_2",
    "content_index": 0,
    "transcript": "练习我的英语口语。",
    "timestamp": "2025-05-27T03:51:42.366Z"
  },
  {
    "type": "conversation.item.created",
    "event_id": "event_5",
    "previous_item_id": "item_UserB_2",
    "item": {
      "id": "item_Assistant2",
      "object": "realtime.item",
      "type": "message",
      "status": "in_progress",
      "role": "assistant",
      "content": []
    },
    "timestamp": "2025-05-27T03:51:42.584Z"
  },
  {
    "type": "response.audio_transcript.delta",
    "event_id": "event_6",
    "response_id": "resp_1",
    "item_id": "item_Assistant2",
    "output_index": 0,
    "content_index": 0,
    "delta": "[eng] Great! Let's start",
    "timestamp": "2025-05-27T03:51:42.598Z"
  },
  {
    "type": "response.audio_transcript.delta",
    "event_id": "event_7",
    "response_id": "resp_1",
    "item_id": "item_Assistant2",
    "output_index": 0,
    "content_index": 0,
    "delta": " practicing English speaking!",
    "timestamp": "2025-05-27T03:51:42.700Z"
  },
  {
    "type": "response.done",
    "event_id": "event_8",
    "response": {
      "object": "realtime.response",
      "id": "resp_1",
      "status": "completed",
      "output": [{
        "id": "item_Assistant2",
        "object": "realtime.item",
        "type": "message",
        "status": "completed",
        "role": "assistant",
        "content": [{
          "type": "audio",
          "transcript": "[eng] Great! Let's start practicing English speaking!"
        }]
      }],
      "usage": {
        "total_tokens": 1316,
        "input_tokens": 1211,
        "output_tokens": 105
      }
    },
    "timestamp": "2025-05-27T03:51:43.341Z"
  }
]`;
    setEventsJson(userBMergeEvents);
    setParseError(null);
  };

  // 加载宽松合并的测试用例（AI跳过输入后的智能合并，基于真实数据）
  const handleLoadSkippedInputExample = () => {
    const skippedInputEvents = `[
  {
    "type": "conversation.item.created",
    "event_id": "event_BbfdWPurI4r51mnH9ZEwR",
    "previous_item_id": null,
    "item": {
      "id": "item_BbfdOkVCOwhwqQc6bbx4D",
      "object": "realtime.item",
      "type": "message",
      "status": "completed",
      "role": "user",
      "content": [
        {
          "type": "input_audio",
          "transcript": null
        }
      ]
    },
    "timestamp": "2025-05-27T03:51:23.000Z"
  },
  {
    "type": "conversation.item.input_audio_transcription.completed",
    "event_id": "event_BbfdUA0h6F3AKzRoWnJNm",
    "item_id": "item_BbfdOkVCOwhwqQc6bbx4D",
    "content_index": 0,
    "transcript": "我想学习英语,我最近发现没人跟我一起说英语。",
    "timestamp": "2025-05-27T03:51:23.210Z"
  },
  {
    "type": "conversation.item.created",
    "event_id": "event_BbfdWPurI4r51mnH9ZEwR2",
    "previous_item_id": "item_BbfdOkVCOwhwqQc6bbx4D",
    "item": {
      "id": "item_BbfdUvzGZWz55NikHFSzc",
      "object": "realtime.item",
      "type": "message",
      "status": "completed",
      "role": "user",
      "content": [
        {
          "type": "input_audio",
          "transcript": null
        }
      ]
    },
    "timestamp": "2025-05-27T03:51:25.020Z"
  },
  {
    "type": "conversation.item.input_audio_transcription.completed",
    "event_id": "event_BbfdWA9gTApmNBr1rn6Nl",
    "item_id": "item_BbfdUvzGZWz55NikHFSzc",
    "content_index": 0,
    "transcript": "English, Polish, Swedish, German",
    "timestamp": "2025-05-27T03:51:25.378Z"
  },
  {
    "type": "conversation.item.created",
    "event_id": "event_BbfdWdlNqkFpG7KXICmAH",
    "previous_item_id": "item_BbfdUvzGZWz55NikHFSzc",
    "item": {
      "id": "item_BbfdWz0bPZuNSFZ6SJwaW",
      "object": "realtime.item",
      "type": "message",
      "status": "in_progress",
      "role": "assistant",
      "content": []
    },
    "timestamp": "2025-05-27T03:51:25.719Z"
  },
  {
    "type": "response.audio_transcript.delta",
    "event_id": "event_BbfdWmTb3kXjo6sM3Ov3U",
    "response_id": "resp_BbfdW4sF0xBxT5FPnzQqR",
    "item_id": "item_BbfdWz0bPZuNSFZ6SJwaW",
    "output_index": 0,
    "content_index": 0,
    "delta": "[eng] I want to learn English, but I've recently found that no one speaks English with me. Can you help me practice?",
    "timestamp": "2025-05-27T03:51:25.733Z"
  },
  {
    "type": "response.done",
    "event_id": "event_BbfdYMMPC9ZUJu4WCrSsq",
    "response": {
      "object": "realtime.response",
      "id": "resp_BbfdW4sF0xBxT5FPnzQqR",
      "status": "completed",
      "output": [
        {
          "id": "item_BbfdWz0bPZuNSFZ6SJwaW",
          "object": "realtime.item",
          "type": "message",
          "status": "completed",
          "role": "assistant",
          "content": [
            {
              "type": "audio",
              "transcript": "[eng] I want to learn English, but I've recently found that no one speaks English with me. Can you help me practice?"
            }
          ]
        }
      ],
      "usage": {
        "total_tokens": 1155,
        "input_tokens": 938,
        "output_tokens": 217
      }
    },
    "timestamp": "2025-05-27T03:51:27.270Z"
  }
]`;
    setEventsJson(skippedInputEvents);
    setParseError(null);
  };

  return (
    // h-screen 天坑，千万不能写 h-screen，否则会撑满整个屏幕，导致页面嵌套滚动
    <div className="flex flex-col bg-background relative">
      {/* 🔑 顶部标题栏 - 移动端隐藏，桌面端显示完整标题 */}
      <div className="hidden sm:flex items-center gap-3 px-4 py-3 border-b bg-white">
        <Button
          onClick={handleBack}
          variant="ghost"
          size="sm"
          className="gap-2"
          disabled={isStopping}
        >
          {isStopping ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('common.loading')}
            </>
          ) : (
            <>
              <CornerUpLeft className="h-4 w-4" />
              {/* 结束翻译 */}
            </>
          )}
        </Button>
        <div className="flex-1">
          <h1 className="text-lg font-semibold">{t('nav.translation')}</h1>
          <p className="text-sm text-muted-foreground">
            {isStopping ? t('translation.stopping') : t('translation.speakNow')}
          </p>
        </div>
      </div>

      {/* 🔑 移动端浮动返回按钮 - 右上角 */}
      <div className="sm:hidden fixed top-2 right-2 z-50">
        <Button
          onClick={handleBack}
          variant="outline"
          size="sm"
          className="rounded-full w-8 h-8 p-0 bg-white/90 backdrop-blur-sm shadow-lg"
          disabled={isStopping}
        >
          {isStopping ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <CornerUpLeft className="h-3 w-3" />
          )}
        </Button>
      </div>

      {/* 🔑 免费试用说明 banner - 录音页面显示紧凑模式 */}
      <FreeTrialNoticeBanner compact={true} />

      {/* 调试面板 - 只在开发模式下显示 */}
      {import.meta.env.DEV && (
        <div className="border-b bg-white">
          <div className="flex items-center justify-between px-4 py-2">
            <button
              onClick={() => setShowDebugPanel(!showDebugPanel)}
              className="flex items-center gap-2 px-3 py-1 text-sm font-medium text-orange-600 hover:text-orange-700 hover:bg-orange-50 rounded-md transition-colors"
            >
              <Bug className="w-4 h-4" />
              事件测试面板
              <span className="text-xs text-gray-500">
                {showDebugPanel ? '收起' : '展开'}
              </span>
            </button>
            {rtcEvents.length > 0 && (
              <span className="text-xs text-green-600 font-medium">
                使用测试事件 ({rtcEvents.length} 个)
              </span>
            )}
          </div>
          
          {showDebugPanel && (
            <div className="border-t bg-gray-50 p-4 space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  {t('debug.eventData')}:
                </label>
                <textarea
                  value={eventsJson}
                  onChange={(e) => setEventsJson(e.target.value)}
                  className="w-full h-32 p-3 border border-gray-300 rounded-md text-sm font-mono resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={t('debug.inputPlaceholder')}
                />
                {parseError && (
                  <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
                    {t('debug.parseError')}: {parseError}
                  </div>
                )}
              </div>
              
              <div className="space-y-3">
                {/* 测试用例按钮组 */}
                <div className="flex flex-wrap gap-2">
                  <Button
                    onClick={() => {
                      setEventsJson(EXAMPLE_EVENTS);
                    }}
                    variant="outline"
                    size="sm"
                    className="text-blue-600 border-blue-300 hover:bg-blue-50"
                  >
                    📝 基础示例
                  </Button>
                  <Button
                    onClick={handleLoadOrphanExample}
                    variant="outline"
                    size="sm"
                    className="text-orange-600 border-orange-300 hover:bg-orange-50"
                  >
                    🔗 落单场景
                  </Button>
                  <Button
                    onClick={handleLoadUserAMergeExample}
                    variant="outline"
                    size="sm"
                    className="text-green-600 border-green-300 hover:bg-green-50"
                  >
                    👤 User A 合并
                  </Button>
                  <Button
                    onClick={handleLoadUserBMergeExample}
                    variant="outline"
                    size="sm"
                    className="text-purple-600 border-purple-300 hover:bg-purple-50"
                  >
                    👤 User B 合并
                  </Button>
                  <Button
                    onClick={handleLoadSkippedInputExample}
                    variant="outline"
                    size="sm"
                    className="text-red-600 border-red-300 hover:bg-red-50"
                  >
                    🔗 宽松合并
                  </Button>
                </div>
                
                {/* 控制按钮 */}
                <div className="flex gap-2">
                  <Button
                    onClick={handleParseEvents}
                    size="sm"
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {t('debug.testAndParse')}
                  </Button>
                  <Button
                    onClick={handleClearEvents}
                    variant="outline"
                    size="sm"
                  >
                    {t('debug.clearEvents')}
                  </Button>
                  <Button
                    onClick={() => {
                      setEventsJson('[]');
                      setParseError(null);
                    }}
                    variant="outline"
                    size="sm"
                    className="text-gray-600"
                  >
                    🗑️ {t('debug.clearAll')}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 中间：聊天气泡列表（可滚动） */}
      <div className="flex-1 overflow-y-auto bg-muted/20" data-fade>
        <UserConversation 
          // key={conversationKey} // 强制重新渲染以清除内部缓存
          // isSessionActive={isSessionActive}
          // sessionConnectionStatus={sessionConnectionStatus}
          // events={displayEvents}
        />
      </div>
    </div>
  );
}; 